import { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";

export const SavePromptModal = ({ show, onHide, currentPromptData, onPromptSaved }) => {
  const [formData, setFormData] = useState({
    name: "",
    id: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Update form data when currentPromptData changes (when a prompt is loaded)
  useEffect(() => {
    if (currentPromptData?.id) {
      setFormData({
        name: currentPromptData.name || "",
        id: currentPromptData.id
      });
    } else {
      setFormData({
        name: "",
        id: null
      });
    }
  }, [currentPromptData?.id, currentPromptData?.name]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (show) {
      if (currentPromptData?.id) {
        setFormData({
          name: currentPromptData.name || "",
          id: currentPromptData.id
        });
      } else {
        setFormData({
          name: "",
          id: null
        });
      }
    }
  }, [show, currentPromptData?.id, currentPromptData?.name]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!currentPromptData.prompt) {
      setError("Prompt field can't be empty.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        id: formData.id,
        name: formData.name,
        prompt: currentPromptData.prompt,
        llm_provider: currentPromptData.llm_provider,
        llm_model: currentPromptData.llm_model,
        temperature: currentPromptData.temperature || 0
      };

      const response = await request("explanation/prompt/save", {
        payload
      });

      // Call the callback with the saved prompt data
      if (onPromptSaved) {
        const savedPromptData = {
          id: response?.id || formData.id, // Use response ID if available, fallback to form ID
          name: formData.name,
          prompt: currentPromptData.prompt,
          llm_provider: currentPromptData.llm_provider,
          llm_model: currentPromptData.llm_model,
          temperature: currentPromptData.temperature || 0
        };
        onPromptSaved(savedPromptData);
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onHide();
      }, 2000);

    } catch (e) {
      console.error("Error saving prompt:", e);
      logError("Admin prompt save failed", e, { category: LogCategory.ERROR });
      setError(e?.detail || e?.message || "An unknown error occurred while saving the prompt.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {formData.id ? "Update Explanation Prompt" : "Save New Explanation Prompt"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {success && (
          <Alert variant="success">
            Prompt {formData.id ? "updated" : "saved"} successfully!
          </Alert>
        )}
        
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSave}>
          <Form.Group className="mb-3">
            <Form.Label>Prompt Name *</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter a name for this prompt"
              required
              disabled={isLoading}
            />
            <Form.Text className="text-muted">
              Choose a descriptive name to easily identify this prompt later.
            </Form.Text>
          </Form.Group>

          <div className="mb-3">
            <strong>Current Prompt Configuration:</strong>
            <div className="mt-2 p-3 bg-light rounded">
              <div><strong>LLM Provider:</strong> {currentPromptData.llm_provider || "Not set"}</div>
              <div><strong>LLM Model:</strong> {currentPromptData.llm_model || "Not set"}</div>
              <div><strong>Temperature:</strong> {currentPromptData.temperature || 0}</div>
              <div><strong>Prompt:</strong> {currentPromptData.prompt ? `${currentPromptData.prompt.substring(0, 100)}...` : "Not set"}</div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading}>
              {isLoading && <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />}
              {formData.id ? "Update Prompt" : "Save Prompt"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
